import { useProjectId, useUpdateBlobMutation } from '@consigli/hooks';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FoldersContent } from '@/pages/insight/folders/content/folders-content';
import { useDragState } from '@/pages/insight/folders/hooks/use-drag-state';
import { FoldersSidebar } from '@/pages/insight/folders/sidebar/folders-sidebar';
import { useFileTreeContext } from '@/pages/insight/folders/use-file-tree-context';
import { Tree, getNonLeafNodes } from '@/pages/insight/folders/util/document-list-to-tree';
import { handleUpdateBlobs } from '@/pages/insight/folders/utils/blob-update-utils';
import {
  snapCenterToCursor,
  handleDragStart,
  handleDragEnd,
} from '@/pages/insight/folders/utils/drag-and-drop-utils';

import { ConfirmDragAndDropPopup } from './content/confirm-drag-popup';

export const FoldersContainer = () => {
  const { activeFolderFiles, tree, activeFolderId } = useFileTreeContext();
  const projectId = useProjectId();
  const [updateBlob] = useUpdateBlobMutation();

  //folders return all folders of the tree, but without assigned blobs
  const folders = getNonLeafNodes(tree as Tree);
  const { t } = useTranslation();

  const {
    draggingCardId,
    setDraggingCardId,
    selectedFiles,
    setSelectedFiles,
    confirmationPopupIsOpen,
    setConfirmationPopupIsOpen,
    activeDraggedFile,
    setActiveDraggedFile,
    selectedFolderForDrag,
    setSelectedFolderForDrag,
  } = useDragState();

  const activeFolder = useMemo(
    () => folders.find((folder) => folder.id === activeFolderId) ?? null,
    [folders, activeFolderId],
  );

  const onDragStart = (event: DragStartEvent | null) => {
    handleDragStart(event, selectedFiles, setSelectedFiles, setDraggingCardId);
  };

  const onDragEnd = async (event: DragEndEvent) => {
    await handleDragEnd(
      event,
      activeFolderFiles,
      setActiveDraggedFile,
      setSelectedFolderForDrag,
      setDraggingCardId,
      setConfirmationPopupIsOpen,
      (
        operation: 'copy' | 'move',
        folderId: UniqueIdentifier,
        file?: (typeof activeFolderFiles)[0],
      ) =>
        handleUpdateBlobs({
          operation,
          selectedFolderForDrag: String(folderId),
          currentDraggedFile: file,
          activeDraggedFile,
          activeFolder,
          selectedFiles,
          draggingCardId: String(draggingCardId),
          projectId,
          t,
          updateBlob,
          onDragStart,
          setConfirmationPopupIsOpen,
        }),
    );
  };

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(PointerSensor),
  );

  const executeUpdateBlobs = (
    operation: 'copy' | 'move',
    targetFolderId: string,
    currentDraggedFile?: (typeof activeFolderFiles)[0],
  ) => {
    return handleUpdateBlobs({
      operation,
      selectedFolderForDrag: targetFolderId,
      currentDraggedFile,
      activeDraggedFile,
      activeFolder,
      selectedFiles,
      draggingCardId: String(draggingCardId),
      projectId,
      updateBlob,
      t,
      onDragStart,
      setConfirmationPopupIsOpen,
    });
  };

  return (
    <>
      <div className="overflow-hidden">
        <DndContext
          sensors={sensors}
          modifiers={[snapCenterToCursor, restrictToWindowEdges]}
          onDragEnd={(event) => {
            onDragEnd(event);
          }}
          onDragStart={onDragStart}
        >
          <div className="flex">
            {confirmationPopupIsOpen && (
              <ConfirmDragAndDropPopup
                onClose={() => {
                  setConfirmationPopupIsOpen(false);
                  onDragStart(null);
                }}
                onMove={() => executeUpdateBlobs('move', String(selectedFolderForDrag))}
                onCopy={() => executeUpdateBlobs('copy', String(selectedFolderForDrag))}
              />
            )}
            <FoldersSidebar />
            <FoldersContent
              draggingCardId={draggingCardId}
              activeFolder={activeFolder}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
            />
          </div>
        </DndContext>
        <div id="drag-card"></div>
      </div>
    </>
  );
};
